/**
 * FormA Converters — Pure utility functions for the access blob + editor toggle state.
 *
 * The canonical source of truth is the access blob (consent/ceiling). These helpers derive
 * the editor's internal toggle state (verb_caps / scope_filters / schema_grants / plugin_ids)
 * FROM the blob (the `accessBlobTo*` family — JS twins of the backend's `to_legacy`), plus
 * the FORWARD fold (`legacyToAccessBlob` — JS twin of the backend's `from_legacy`) and a blob
 * → YAML serializer, plus intersection/subset logic for realm ↔ EP ↔ DLR validation. On save
 * the editor now builds the blob CLIENT-SIDE and stringifies the BLOB as `consent_yaml` /
 * `ceiling_yaml` — it no longer round-trips legacy toggle state through `/access/preview-yaml`.
 */

import * as YAML from 'yaml';
import { VERBS_BY_RESOURCE, EMPTY_VERB_CAPS, FULL_VERB_CAPS } from '@paradigm/permissions/constants/formA';

/**
 * Returns a fresh empty verb_caps object (all arrays empty).
 * Derived from EMPTY_VERB_CAPS so adding a new resource in formA.js
 * automatically propagates here.
 */
export function emptyVerbCaps() {
  return Object.fromEntries(Object.keys(EMPTY_VERB_CAPS).map(r => [r, []]));
}

// Node verb -> the editor's scope_filters clause key (mirrors backend _NODE_VERB_CLAUSE).
// The editor's per-verb scope dict (`scopeFilters`) is keyed by the canonical `${resource}:${verb}`
// — the SAME key as REQUEST_TYPE_REGISTRY. There is no separate clause vocabulary.
const _sfKey = (resource, verb) => `${resource}:${verb}`;
const _NODE_SF_VERBS = ['read', 'create', 'edit', 'delete', 'propose'];

/**
 * Derive the editor's verb_caps shape ({resource: [verb]}) from the canonical access blob.
 * The granted verbs for a resource are the keys of `blob[resource]`. Canonical node write
 * verb is `edit` (NOT the legacy `edit_content`). This is the JS twin of the backend's
 * to_legacy verb extraction, kept verbatim so reopening an editor shows what's enforced.
 */
export function accessBlobToVerbCaps(blob) {
  const caps = emptyVerbCaps();
  if (!blob || typeof blob !== 'object') return caps;
  for (const resource of Object.keys(caps)) {
    const res = blob[resource];
    if (res && typeof res === 'object') caps[resource] = Object.keys(res);
  }
  return caps;
}

/**
 * Derive the union of node ids scoped across all node verbs in the blob — the new-model
 * equivalent of the legacy `selected_node_ids`/`allowed_node_ids` column (which was shared
 * across every node verb). Mirrors the backend to_legacy node_ids union.
 */
export function accessBlobToNodeIds(blob) {
  const nodes = blob?.nodes;
  if (!nodes || typeof nodes !== 'object') return [];
  const ids = new Set();
  for (const scope of Object.values(nodes)) {
    for (const nid of (Array.isArray(scope?.nodes) ? scope.nodes : [])) ids.add(String(nid));
  }
  return [...ids];
}

/**
 * Derive the union of schema ids scoped across all node verbs in the blob — the new-model
 * equivalent of the legacy `selected_schema_ids`/`allowed_schema_ids` column.
 */
export function accessBlobToSchemaIds(blob) {
  const nodes = blob?.nodes;
  if (!nodes || typeof nodes !== 'object') return [];
  const ids = new Set();
  for (const scope of Object.values(nodes)) {
    for (const sid of Object.keys(scope?.schemas || {})) ids.add(String(sid));
  }
  return [...ids];
}

/**
 * Derive the plugin id list from the blob (`blob.plugins.execute.plugin_ids`).
 */
export function accessBlobToPluginIds(blob) {
  const ids = blob?.plugins?.execute?.plugin_ids;
  return Array.isArray(ids) ? ids.map(String) : [];
}

/**
 * Per-tool modes from the blob (`blob.plugins.execute.tools`).
 *
 * Shape: `{ "<pluginId>": { "<toolId>": "allow" | "ask" | "deny" } }`.
 *
 * Returns `{}` when absent, which is NOT "everything denied" — it is "this grant
 * was written before tools existed, or says nothing about them", and the plugin
 * grant stands unchanged. That asymmetry is the whole reason old grants did not
 * change meaning when tools arrived, and reading absence as denial here would
 * quietly revoke every realm a person already had.
 */
export function accessBlobToToolModes(blob) {
  const raw = blob?.plugins?.execute?.tools;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [pid, modes] of Object.entries(raw)) {
    if (!modes || typeof modes !== 'object') continue;
    const kept = {};
    for (const [tid, mode] of Object.entries(modes)) {
      // Anything unrecognised is dropped rather than shown: a mode the editor
      // cannot render is one a person cannot check, and displaying it as its
      // nearest neighbour would misreport what they granted.
      if (mode === 'allow' || mode === 'ask' || mode === 'deny') kept[tid] = mode;
    }
    if (Object.keys(kept).length) out[String(pid)] = kept;
  }
  return out;
}

/**
 * Derive read-time field masks (schema_grants shape) from the blob. Masks live on
 * `blob.nodes.read.schemas[id] = {fields, filter}` — keyed by schema ID. The legacy column
 * was schema-NAME keyed, and the SchemaGrantsPanel keys by name/URI, so pass an optional
 * `idToName` map (or a `schemas` array of {id, schema_name}) to re-key to names; without it
 * the masks stay ID-keyed. Returns null when there are no non-empty masks. JS twin of the
 * backend to_legacy schema_grants extraction.
 */
export function accessBlobToSchemaGrants(blob, idToName = null) {
  const schemas = blob?.nodes?.read?.schemas;
  if (!schemas || typeof schemas !== 'object') return null;
  let nameOf = null;
  if (Array.isArray(idToName)) {
    const m = {};
    for (const s of idToName) if (s && s.id != null) m[String(s.id)] = s.schema_name || s.name;
    nameOf = (sid) => m[String(sid)] || sid;
  } else if (idToName && typeof idToName === 'object') {
    nameOf = (sid) => idToName[String(sid)] || sid;
  }
  const grants = {};
  for (const [sid, mask] of Object.entries(schemas)) {
    if (mask && typeof mask === 'object' && (mask.fields || mask.filter)) {
      grants[nameOf ? nameOf(sid) : sid] = mask;
    }
  }
  return Object.keys(grants).length ? grants : null;
}

/**
 * Derive the editor's `scope_filters` from the canonical access blob (consent/ceiling) — the
 * NEW-MODEL source of truth. The editor reconstructs each verb's scope from the per-clause
 * scope_filters; the blob holds it per verb under `<resource>.<verb>.{...}`. We rebuild ALL
 * clauses (node, activity) from the blob — NOT the legacy `scope_filters`
 * column, which is being dropped. This is the JS twin of the backend's to_legacy scope_filters
 * extraction. `baseScopeFilters` is an optional seed for non-blob clauses (e.g. `discover`,
 * which carries no scope); blob-derived clauses always win.
 */
export function accessBlobToScopeFilters(blob, baseScopeFilters = {}) {
  const sf = {};
  // Seed with any non-derivable scope keys (anything not produced from the blob below).
  const _DERIVED = new Set([
    ..._NODE_SF_VERBS.map(v => _sfKey('nodes', v)),
    _sfKey('activity', 'read'),
  ]);
  for (const [k, v] of Object.entries(baseScopeFilters || {})) {
    if (!_DERIVED.has(k)) sf[k] = v;
  }
  const access = blob || {};
  // ── node scope: nodes.<verb>.{schemas,nodes} → sf['nodes:<verb>'] ──
  const nodes = access.nodes || {};
  for (const [verb, scope] of Object.entries(nodes)) {
    if (!_NODE_SF_VERBS.includes(verb) || !scope || typeof scope !== 'object') continue;
    const key = _sfKey('nodes', verb);
    const schemaIds = scope.schemas ? Object.keys(scope.schemas) : [];
    const nodeIds = Array.isArray(scope.nodes) ? scope.nodes : [];
    // include_refs must survive the round trip. It is the successor to the retired
    // `relationships:read` for UNSCOPED grants, and dropping it here would silently
    // strip graph shape from every EP that has one the moment someone opened the
    // editor and saved — 36 EPs locally, 93 in prod.
    if (schemaIds.length || nodeIds.length || scope.include_refs) {
      sf[key] = {};
      if (schemaIds.length) sf[key].schema_ids = schemaIds;
      if (nodeIds.length) sf[key].node_ids = nodeIds;
      if (scope.include_refs) sf[key].include_refs = true;
    }
  }
  // ── activity scope: activity.read.{categories,actor_types} → sf['activity:read'] ──
  const actRead = access.activity?.read;
  if (actRead && typeof actRead === 'object') {
    const s = {};
    if (Array.isArray(actRead.categories)) s.categories = [...actRead.categories];
    if (Array.isArray(actRead.actor_types)) s.actor_types = [...actRead.actor_types];
    if (Object.keys(s).length) sf[_sfKey('activity', 'read')] = s;
  }
  return sf;
}

// ── FORWARD: editor toggle state → canonical access blob ─────────────────────
// JS twin of the backend's authz.legacy_translate.from_legacy. The frontend now folds its
// own editor toggle state (verbs + scope_filters + selected schema/node/plugin ids + field
// masks) into the canonical access blob CLIENT-SIDE, then serializes the BLOB to YAML — the
// backend no longer folds legacy→blob on the preview-yaml input path for these save flows.

// legacy node verb -> canonical node verb (rename edit_content -> edit)
const _NODE_VERB_IN = {
  read: 'read', edit_content: 'edit', edit: 'edit', create: 'create',
  delete: 'delete', propose: 'propose',
};

/**
 * Build a name/uri -> schema id map from a `schemas` array of {id, schema_name|name|uri}.
 * Tolerant: indexes by every plausible name key so a grant keyed by schema_name, uri, or
 * name all resolve. Returns {} for falsy/non-array input.
 */
export function buildNameToId(schemas) {
  const m = {};
  if (!Array.isArray(schemas)) return m;
  for (const s of schemas) {
    if (!s || s.id == null) continue;
    const id = String(s.id);
    for (const k of [s.schema_name, s.name, s.uri]) {
      if (k) m[String(k)] = id;
    }
  }
  return m;
}

/**
 * Fold editor toggle state into the canonical access blob (consent/ceiling shape):
 *   { resource: { verb: {...scope} } }
 * Faithful JS port of backend from_legacy. Inputs mirror the legacy editor fields:
 *   verbs        : verb_caps / enabled_verbs   {resource: [verb]}
 *   schemaIds    : selected_schema_ids / allowed_schema_ids   [id]  (applies to ALL node verbs)
 *   nodeIds      : selected_node_ids / allowed_node_ids        [id]  (applies to ALL node verbs)
 *   pluginIds    : selected_plugin_ids / allowed_plugin_ids    [id]
 *   schemaGrants : {schemaName|id: {fields, filter}}  read-time masks
 *   scopeFilters : {clause: {...}}   per-verb node scope + rel/activity scope
 *   nameToId     : schemaName -> id map (or a `schemas` array) for resolving schema_grants
 * Canonicalizes legacy `edit_content` -> `edit`. Empty selected_* + empty clause = unrestricted.
 */
export function legacyToAccessBlob({
  verbs = {}, schemaIds = [], nodeIds = [], pluginIds = [], toolModes = null,
  schemaGrants = null, scopeFilters = null, nameToId = null,
  scopeChoices = null, flatSchemaVerbs = null,
} = {}) {
  verbs = verbs || {};
  const baseSchemaIds = (schemaIds || []).map(String);
  const baseNodeIds = (nodeIds || []).map(String);
  const baseSchemaSet = new Set(baseSchemaIds);
  const baseNodeSet = new Set(baseNodeIds);
  const plugIds = (pluginIds || []).map(String);
  const grants = schemaGrants || {};
  const sf = scopeFilters || {};
  const idMap = Array.isArray(nameToId) ? buildNameToId(nameToId) : (nameToId || {});

  // resolve read-time masks (keyed by schema NAME, or already an id) -> schema id
  const grantsById = {};
  for (const [key, grant] of Object.entries(grants)) {
    if (!grant || typeof grant !== 'object' || (!grant.fields && !grant.filter)) continue;
    const sid = idMap[key] != null ? String(idMap[key]) : String(key);
    grantsById[sid] = grant;
  }

  const access = {};

  // ── nodes ──────────────────────────────────────────────────────────────────
  const nodeVerbs = verbs.nodes || [];
  if (nodeVerbs.length) {
    const nodesOut = {};
    for (const legacyVerb of nodeVerbs) {
      const verb = _NODE_VERB_IN[legacyVerb];
      if (!verb) continue;
      const clause = sf[_sfKey('nodes', verb)];
      const clauseObj = (clause && typeof clause === 'object') ? clause : {};
      // flatSchemaVerbs (opt-in) confines the flat schemaIds list to the verbs it
      // belongs on (e.g. ['read']): per-verb clauses then govern the other verbs,
      // preserving a DLR's per-verb schema scoping instead of flattening it.
      const applyFlat = !flatSchemaVerbs || flatSchemaVerbs.includes(verb);
      const flatIds = applyFlat ? baseSchemaIds : [];
      const flatSet = applyFlat ? baseSchemaSet : new Set();
      const verbSchemaIds = [
        ...flatIds,
        ...((clauseObj.schema_ids || []).map(String).filter(s => !flatSet.has(s))),
      ];
      const verbNodeIds = [
        ...baseNodeIds,
        ...((clauseObj.node_ids || []).map(String).filter(n => !baseNodeSet.has(n))),
      ];
      const scope = {};
      if (verbSchemaIds.length) {
        scope.schemas = {};
        for (const sid of verbSchemaIds) {
          scope.schemas[sid] = (verb === 'read') ? (grantsById[sid] || {}) : {};
        }
      }
      if (verbNodeIds.length) scope.nodes = verbNodeIds;
      if (verb === 'read' && clauseObj.include_refs) scope.include_refs = true;
      // scope_type for write verbs: user's consent choice (own = only app's nodes, any = all)
      if (['edit', 'create', 'delete'].includes(verb) && scopeChoices) {
        // scopeChoices is keyed by schema display name; default to 'own' (safest)
        const choice = Object.values(scopeChoices).includes('any') ? 'any' : 'own';
        scope.scope_type = choice;
      }
      nodesOut[verb] = scope;
    }
    if (Object.keys(nodesOut).length) access.nodes = nodesOut;
  }

  // ── plugins ──────────────────────────────────────────────────────────────--
  if ((verbs.plugins || []).includes('execute')) {
    const scope = plugIds.length ? { plugin_ids: plugIds } : {};
    // THE TOOL IS THE UNIT OF CONSENT, and this is where it survives the fold.
    // `plugin_ids` answers "may this app use Google", which is not a question
    // anybody can answer honestly — Google is a hundred capabilities and reading
    // a calendar is not sending mail. `tools` narrows WITHIN a named plugin:
    //
    //   tools: { "<plugin>": { calendar: "allow", gmail: "ask", drive: "deny" } }
    //
    // Only modes that actually narrow are written. An `allow` on every tool of a
    // plugin already in `plugin_ids` says nothing the plugin grant did not, and
    // writing it would turn "the whole plugin" into a FROZEN LIST — a tool added
    // tomorrow would be silently denied against a grant the person believed was
    // for the plugin. Omission means denial ONLY once a plugin is named here, so
    // naming it costs something and is not done idly.
    const narrowing = {};
    for (const [pid, modes] of Object.entries(toolModes || {})) {
      if (!modes || typeof modes !== 'object') continue;
      // A tool map for a plugin that is not granted is not a narrowing, it is a
      // contradiction — drop it rather than store a grant nothing can read.
      if (plugIds.length && !plugIds.includes(String(pid))) continue;
      const kept = {};
      for (const [tid, mode] of Object.entries(modes)) {
        if (mode === 'ask' || mode === 'deny') kept[tid] = mode;
      }
      // `allow` entries are only meaningful alongside something narrower: they
      // say "these specifically", which is a real statement once any sibling is
      // restricted and pure noise otherwise.
      if (Object.keys(kept).length) {
        for (const [tid, mode] of Object.entries(modes)) {
          if (mode === 'allow') kept[tid] = mode;
        }
        narrowing[String(pid)] = kept;
      }
    }
    if (Object.keys(narrowing).length) scope.tools = narrowing;
    access.plugins = { execute: scope };
  }

  // ── discovery ────────────────────────────────────────────────────────────--
  const disc = verbs.discovery || [];
  if (disc.length) {
    access.discovery = {};
    for (const v of disc) if (v === 'friends' || v === 'groups' || v === 'realms') access.discovery[v] = {};
  }

  // ── activity ─────────────────────────────────────────────────────────────--
  if ((verbs.activity || []).includes('read')) {
    const c = sf[_sfKey('activity', 'read')] || {};
    const s = {};
    if (c.categories != null) s.categories = [...c.categories];
    if (c.actor_types != null) s.actor_types = [...c.actor_types];
    access.activity = { read: s };
  }

  // ── sub_entity ─────────────────────────────────────────────────────────────--
  // Structural verb (no scope). Without this block the consent card renders
  // "Create sub-entities that act for you" but drops it from the submitted consent.
  const subEnt = verbs.sub_entity || [];
  if (subEnt.length) {
    access.sub_entity = {};
    for (const v of subEnt) if (v === 'declare') access.sub_entity[v] = {};
  }

  // ── automations ────────────────────────────────────────────────────────────--
  const auto = verbs.automations || [];
  if (auto.length) {
    access.automations = {};
    for (const v of auto) if (v === 'write' || v === 'execute') access.automations[v] = {};
  }

  return access;
}

/**
 * Serialize a canonical access blob to YAML. The wire format the realm/EP/consent save
 * endpoints accept as `consent_yaml`/`ceiling_yaml` is the blob itself rendered as YAML.
 */
export function accessBlobToYaml(blob) {
  return YAML.stringify(blob || {}, { indent: 2 });
}

/**
 * One-shot: fold editor toggle state to a blob, then serialize to YAML. Convenience for the
 * save flows that previously POSTed legacy state to /access/preview-yaml.
 */
export function legacyToAccessYaml(input) {
  return accessBlobToYaml(legacyToAccessBlob(input));
}

/**
 * Returns a verb_caps object with all verbs enabled.
 */
export function fullVerbCaps() {
  return Object.fromEntries(
    Object.entries(FULL_VERB_CAPS).map(([r, verbs]) => [r, [...verbs]])
  );
}

/**
 * Normalize a verb_caps object — ensure all resource keys exist with arrays, and canonicalize
 * the node write verb (legacy `edit_content` → `edit`). The canonicalization lets the editor
 * ingest any legacy-shaped verbs that still arrive on a non-blob path (e.g. the YAML import
 * endpoint, which folds the ceiling via the backend's to_legacy and emits `edit_content`).
 */
export function normalizeVerbCaps(caps) {
  if (!caps || typeof caps !== 'object') return emptyVerbCaps();
  const result = emptyVerbCaps();
  for (const resource of Object.keys(result)) {
    if (Array.isArray(caps[resource])) {
      result[resource] = [...caps[resource]];
    }
  }
  // Canonicalize the legacy node write verb.
  result.nodes = [...new Set(result.nodes.map(v => (v === 'edit_content' ? 'edit' : v)))];
  return result;
}

/**
 * Compute intersection of two verb_caps objects.
 * Returns only verbs present in BOTH. Used for realm ∩ DLR.
 */
export function intersectVerbCaps(capsA, capsB) {
  const a = normalizeVerbCaps(capsA);
  const b = normalizeVerbCaps(capsB);
  const result = emptyVerbCaps();

  for (const resource of Object.keys(result)) {
    const setB = new Set(b[resource]);
    result[resource] = a[resource].filter(v => setB.has(v));
  }

  return result;
}

/**
 * Check if epVerbs is a subset of realmCaps.
 * Returns { valid: boolean, violations: string[] }.
 */
export function isSubsetVerbCaps(epVerbs, realmCaps) {
  const ep = normalizeVerbCaps(epVerbs);
  const realm = normalizeVerbCaps(realmCaps);
  const violations = [];

  for (const resource of Object.keys(ep)) {
    const allowed = new Set(realm[resource]);
    for (const verb of ep[resource]) {
      if (!allowed.has(verb)) {
        violations.push(`${resource}.${verb}`);
      }
    }
  }

  return { valid: violations.length === 0, violations };
}

/**
 * Toggle a verb in a verb_caps object. Returns new object (immutable).
 */
export function toggleVerb(verbCaps, resource, verb) {
  const caps = normalizeVerbCaps(verbCaps);
  const verbs = caps[resource];
  if (!verbs) return caps;

  if (verbs.includes(verb)) {
    caps[resource] = verbs.filter(v => v !== verb);
  } else {
    // Validate verb is valid for this resource
    if (VERBS_BY_RESOURCE[resource]?.includes(verb)) {
      caps[resource] = [...verbs, verb];
    }
  }

  return caps;
}

/**
 * Check if a verb is enabled in verb_caps.
 */
export function hasVerb(verbCaps, resource, verb) {
  const caps = normalizeVerbCaps(verbCaps);
  return caps[resource]?.includes(verb) ?? false;
}

/**
 * Count total enabled verbs across all resources.
 */
export function countVerbs(verbCaps) {
  const caps = normalizeVerbCaps(verbCaps);
  return Object.values(caps).reduce((sum, arr) => sum + arr.length, 0);
}

/**
 * Check if verb_caps is empty (no verbs enabled).
 */
export function isEmpty(verbCaps) {
  return countVerbs(verbCaps) === 0;
}

/**
 * Calculate an ISO expiration timestamp from a duration string.
 * Accepts "1h", "1d", "7d", "30d", "90d", "365d", "never".
 * Returns ISO string or null (for "never").
 */
export function calculateExpiresAt(expirationTime) {
  if (!expirationTime || expirationTime === 'never') return null;
  const now = new Date();
  if (expirationTime.endsWith('h')) now.setHours(now.getHours() + parseInt(expirationTime));
  else if (expirationTime.endsWith('d')) now.setDate(now.getDate() + parseInt(expirationTime));
  return now.toISOString();
}

/**
 * Merge a realm's ceiling blob into a consent access blob, expanding the consent
 * to include all verbs/schemas the realm allows. Used when "share rest of realm"
 * is toggled on — the EP's consent becomes the full realm ceiling.
 *
 * For each resource (nodes, relationships, plugins, discovery, activity), if the
 * ceiling has verbs/schemas not present in the consent, they're added. Existing
 * consent entries (e.g. scope_type, field masks) are preserved.
 */
export function mergeRealmCeiling(consentBlob, ceilingBlob) {
  if (!ceilingBlob || typeof ceilingBlob !== 'object') return consentBlob;
  const merged = JSON.parse(JSON.stringify(consentBlob || {}));

  for (const resource of Object.keys(ceilingBlob)) {
    const ceilResource = ceilingBlob[resource];
    if (!ceilResource || typeof ceilResource !== 'object') continue;

    if (!merged[resource]) merged[resource] = {};

    for (const verb of Object.keys(ceilResource)) {
      const ceilVerb = ceilResource[verb];
      if (!merged[resource][verb]) {
        // Verb not in consent — add it from ceiling
        merged[resource][verb] = JSON.parse(JSON.stringify(ceilVerb || {}));
      } else {
        // Verb exists — merge schemas from ceiling into consent
        const existing = merged[resource][verb];
        if (ceilVerb?.schemas && typeof ceilVerb.schemas === 'object') {
          if (!existing.schemas) existing.schemas = {};
          for (const sid of Object.keys(ceilVerb.schemas)) {
            if (!existing.schemas[sid]) {
              existing.schemas[sid] = JSON.parse(JSON.stringify(ceilVerb.schemas[sid] || {}));
            }
          }
        }
      }
    }
  }

  return merged;
}

/**
 * Verb-ceiling map ({resource:[verb]}) → canonical consent access blob ({resource:{verb:{}}}).
 * Verbs-only ceiling (scope rides the selected_* selectors); maps the legacy `edit_content`
 * spelling to canonical `edit`. Used to send `consent` instead of the legacy `enabled_verbs`.
 */
export function verbCapsToConsent(caps) {
  const blob = {};
  for (const [resource, verbs] of Object.entries(caps || {})) {
    const vb = {};
    for (const v of (verbs || [])) vb[v === 'edit_content' ? 'edit' : v] = {};
    if (Object.keys(vb).length) blob[resource] = vb;
  }
  return blob;
}
